import IconButton from '@material-ui/core/IconButton'
import Tooltip from '@material-ui/core/Tooltip'
import cx from 'classnames'
import React, { useCallback, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'

import * as Actions from 'actions'
import { addSourceFileAtSliderPosition } from 'actions/addSourceFileAtSliderPosition'
import { ReactComponent as AddIcon } from 'assets/media/audio_add.svg'
import { PLAYBACK_STATE, PLAYER_TYPE } from 'enums'
import { useAction } from 'hooks'
import PropTypes from 'prop-types'
import * as PT from '~/PropTypes'
import { refTimeToHHMMSS } from '~/Util'
import { ReactComponent as PlayIcon } from '~/assets/video-player/ic_play_transparent.svg'
import { ReactComponent as StopIcon } from '~/assets/video-player/ic_stop.svg'
import useDraggable from '~/components/AssetPanels/hooks/useDraggable'
import { selectSelectedClipIsBuffering } from '~/selectors'
import { selectSplitSize } from '~/selectors/useInterface'
import styles from './listItem.module.scss'
import { MEDIA_BLOCK_WIDTH_FOR_FIX_AUDIO_ITEM } from '~/constant'
import { useAddSourceFileToTimeline } from '~/hooks/useAddSourceFileToTimeline'
import { UI_ELEMENT } from '~/config/constants/ui'

export default function AudioAssetsListItem(props) {
  const {
    /** @type {Assets.AudioAsset} */
    asset,
    onGenreClick,
    getCategory,
    setUIElementBounds,
    // eslint-disable-next-line no-unused-vars
    ...rest
  } = props
  const sourceAssetRef = useRef()
  const setPlaybackClip = useAction(Actions.playback.setPlaybackClip, asset)
  const setPlayPause = useAction(Actions.playback.togglePlaybackPlaying)
  const selectedClipId = useSelector(state => state.playback.selectedClipId)
  const clipPlaybackState = useSelector(state => state.playback.clipPlaybackState)
  const isBuffering = useSelector(state => selectSelectedClipIsBuffering(state, asset.id))
  const { media: mediaColumnSize } = useSelector(selectSplitSize)
  const onAddToTimeline = useAddSourceFileToTimeline(addSourceFileAtSliderPosition, [ asset.id ])

  const [ , setDraggable ] = useDraggable(asset, {})

  const playing = clipPlaybackState === PLAYBACK_STATE.PLAY && selectedClipId === asset.id

  const category = getCategory(asset) || {}

  const onSelectItem = useCallback(
    () => {
      if (playing) {
        setPlayPause({
          playerType: PLAYER_TYPE.MEDIA,
        })
      } else { setPlaybackClip() }
    },
    [ setPlaybackClip, playing, setPlayPause ]
  )

  const handleSelectGenre = () => {
    onGenreClick(category.id)
  }

  useEffect(() => {
    if (setUIElementBounds && sourceAssetRef.current) {
      const { width, height } = sourceAssetRef.current.getBoundingClientRect()
      const style = window.getComputedStyle(sourceAssetRef.current)
      const marginL = parseFloat(style.marginLeft)
      const marginR = parseFloat(style.marginRight)
      const marginT = parseFloat(style.marginTop)
      const marginB = parseFloat(style.marginBottom)
      setUIElementBounds(UI_ELEMENT.SOURCE_MEDIA_ASSET, {
        width: width + marginL + marginR,
        height: height + marginB + marginT,
      })
    }
  }, [ setUIElementBounds ])

  return (
    <>
      <div
        ref={ref => {
          setDraggable(ref)
          sourceAssetRef.current = ref
        }}
        className={cx(styles.container,
          {
            [styles.playing]: playing,
            [styles.fixSize]: mediaColumnSize > MEDIA_BLOCK_WIDTH_FOR_FIX_AUDIO_ITEM,
          })}
      >
        <div className={cx(styles.track, { [styles.buffering]: isBuffering })}>
          <IconButton
            className={cx(styles.btn, styles.play, {
              [styles.fill]: playing,
              [styles.playIcon]: !playing,
            })}
            onClick={onSelectItem}
            aria-label={playing ? 'Stop' : 'Play'}
          >
            {playing ? <StopIcon /> : <PlayIcon />}
          </IconButton>
          <div className={styles.meta}>
            <Tooltip title={asset.name} classes={{ popper: 'tooltip media-tooltip' }}>
              <div className={styles.title}>{asset.name}</div>
            </Tooltip>
            <div className={styles.genre} onClick={handleSelectGenre}>{category ? category.name : ''}</div>
          </div>
          <IconButton className={cx(styles.btn, styles.add)} onClick={onAddToTimeline}>
            <AddIcon />
          </IconButton>
          <div className={styles.duration}>{refTimeToHHMMSS(asset.duration, true)}</div>
        </div>
        <div className={cx({ [styles.progress]: isBuffering })} />
      </div>
    </>
  )
}

AudioAssetsListItem.defaultProps = {
  setUIElementBounds: null,
}

AudioAssetsListItem.propTypes = {
  asset: PT.AudioAsset.isRequired,
  onGenreClick: PropTypes.func.isRequired,
  getCategory: PropTypes.func.isRequired,
  setUIElementBounds: PropTypes.func,
}
